import { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Upload as UploadIcon, CheckCircle, AlertCircle, Info, Landmark } from 'lucide-react';
import ImportReviewModal from '../components/upload/ImportReviewModal';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { BANK_FORMATS, BANK_FORMAT_LIST, splitRows } from '@/lib/bankFormats';
import { matchAll } from '@/lib/categoryMatcher';

// ─── Chunked bulk create (avoids payload size limits) ────────────────────────
const CHUNK_SIZE = 500;

async function bulkCreateInChunks(rows, onProgress) {
  let done = 0;
  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    const chunk = rows.slice(i, i + CHUNK_SIZE);
    await base44.entities.Transaction.bulkCreate(chunk);
    done += chunk.length;
    if (onProgress) onProgress(done, rows.length);
  }
}

async function fetchAllExisting(onProgress) {
  const PAGE = 5000;
  let all = [], skip = 0;
  for (;;) {
    if (onProgress) onProgress(all.length);
    const page = await base44.entities.Transaction.list('-date', PAGE, skip);
    all = all.concat(page);
    if (page.length < PAGE) break;
    skip += PAGE;
  }
  return all;
}

function dedupKey(t) {
  return `${t.date}|${(t.description || '').toLowerCase().trim()}|${t.amount}`;
}

function deduplicate(incoming, existing) {
  const seen = new Set(existing.map(dedupKey));
  const unique = [];
  for (const t of incoming) {
    const k = dedupKey(t);
    if (seen.has(k)) continue;
    seen.add(k); // also collapses duplicates within the file itself
    unique.push(t);
  }
  return unique;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Upload() {
  const [bankFormat, setBankFormat] = useState('ing');
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState(null); // null | processing | success | error
  const [message, setMessage] = useState('');
  const [review, setReview] = useState(null); // {matched, categories, duplicateCount}
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const format = BANK_FORMATS[bankFormat];

  const processFile = async (file) => {
    if (!file?.name.toLowerCase().endsWith('.csv')) {
      setStatus('error');
      setMessage('Please upload a .csv file.');
      return;
    }

    setStatus('processing');
    setMessage(`Parsing as ${format.label}...`);

    try {
      const text = await file.text();
      const rows = splitRows(text);
      if (!rows.length) {
        setStatus('error');
        setMessage('No data found in the CSV.');
        return;
      }

      const parsed = format.parse(rows);
      if (!parsed.length) {
        setStatus('error');
        setMessage(
          `Parsed ${rows.length} row${rows.length !== 1 ? 's' : ''} but none produced a valid ` +
          `transaction. Check you picked the right bank format above — ${format.hint}.`
        );
        return;
      }

      // Pull history: needed both for duplicate detection and as matcher training data.
      setMessage('Loading your transaction history...');
      const existing = await fetchAllExisting(n =>
        setMessage(`Loading your transaction history... (${n.toLocaleString()} loaded)`)
      );

      const unique = deduplicate(parsed, existing);
      const duplicateCount = parsed.length - unique.length;

      if (!unique.length) {
        setStatus('error');
        setMessage(
          `All ${parsed.length.toLocaleString()} transactions in this file are already in your ` +
          `history — nothing new to import.`
        );
        return;
      }

      setMessage(`Matching ${unique.length.toLocaleString()} transactions against your history...`);
      const matched = matchAll(unique, existing);

      const categories = [...new Set(existing.map(t => t.category).filter(Boolean))].sort();

      setStatus(null);
      setMessage('');
      setReview({ matched, categories, duplicateCount });
    } catch (err) {
      setStatus('error');
      setMessage(`Could not process the file: ${err.message}`);
    }
  };

  const handleConfirmImport = async (finalTransactions) => {
    setIsSaving(true);
    try {
      await bulkCreateInChunks(finalTransactions);
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['lastUpload'] });
      setReview(null);
      setStatus('success');
      setMessage(`Imported ${finalTransactions.length.toLocaleString()} transactions.`);
      setTimeout(() => navigate(createPageUrl('Dashboard')), 1200);
    } catch (err) {
      setStatus('error');
      setMessage(`Import failed: ${err.message}`);
      setReview(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFilePick = (e) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
    e.target.value = ''; // allow re-picking the same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800">Import Transactions</h1>
        <p className="text-sm text-slate-400 mt-1">
          Categories are matched automatically from your existing history
        </p>
      </div>

      {/* Bank format picker */}
      <div className="mb-5">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
          <Landmark className="w-3.5 h-3.5 text-slate-400" />
          Which bank is this export from?
        </label>
        <select
          value={bankFormat}
          onChange={e => setBankFormat(e.target.value)}
          className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700"
        >
          {BANK_FORMAT_LIST.map(f => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <p className="text-xs text-slate-400 mt-1.5">{format.hint}</p>
      </div>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all select-none ${
          isDragging
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50/60'
        } ${status === 'processing' ? 'pointer-events-none opacity-60' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFilePick} />
        <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <UploadIcon className="w-7 h-7 text-slate-400" />
        </div>
        <p className="font-semibold text-slate-700 mb-1">Drop your CSV file here</p>
        <p className="text-sm text-slate-400 mb-5">or click to browse files</p>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
        >
          Choose File
        </Button>
      </div>

      {/* Status */}
      {status && (
        <div className={`mt-5 p-4 rounded-xl flex items-start gap-3 border text-sm font-medium ${
          status === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700'
          : status === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          {status === 'processing' && <div className="w-4 h-4 mt-0.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />}
          {status === 'success' && <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          {status === 'error' && <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{message}</span>
        </div>
      )}

      {/* How matching works */}
      <div className="mt-8 p-5 bg-slate-50 rounded-xl border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-700">How category matching works</h3>
        </div>
        <ul className="space-y-1.5 text-xs text-slate-500">
          {[
            ['Exact', 'the description matches a previous transaction once receipt numbers and dates are stripped'],
            ['Merchant', 'same merchant or transfer destination account as a previous transaction'],
            ['Similar', 'partial word overlap with something you have categorised before — worth checking'],
            ['No match', 'never seen before, so you will be asked to categorise it'],
          ].map(([tier, note]) => (
            <li key={tier} className="flex items-start gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 mt-1.5" />
              <span><span className="font-medium text-slate-600">{tier}</span> — {note}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-slate-400 mt-3">
          You review every match before anything is saved. Corrections you make become
          training data for the next import, so accuracy improves over time. Transactions
          already in your history are skipped automatically.
        </p>
      </div>

      {review && (
        <ImportReviewModal
          matched={review.matched}
          categories={review.categories}
          duplicateCount={review.duplicateCount}
          onConfirm={handleConfirmImport}
          onCancel={() => setReview(null)}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
